import styled from "@emotion/styled";

import UIcon from "lib/icons/untitled_ui/UIcon";
import { sysTextSecondary } from "lib/themes/colors";
import { DataTypeNameWithBuyersAndSuppliers } from "lib/types/models";
import { capitalize } from "../../lib/utils";
import { Flex } from "../../styles/utility-components";

export function Logo({ url, size = 24 }: { url?: string | null; size?: number }) {
  return url ? (
    <img aria-label="organisation-logo" src={url} height={size} width={size} />
  ) : (
    <UIcon icon="wallet02" color={sysTextSecondary} size={size} />
  );
}

export type AllEntities =
  | "notice"
  | "document"
  | "framework"
  | "buyer"
  | "supplier"
  | "contact"
  // Cognism-sourced contact: saved via its redeemId (redeem-on-save), unlike an
  // Oscar "contact" which is saved by oscar_contacts.id. Both resolve to a Contact lead.
  | "contact_cognism"
  | "buyer_activity_report"
  | "custom_buyer";

export type SharedSaveProps<T> = {
  entityType: AllEntities;
  entityId: string;
  // The stored lead entity_id for the "already saved to lead" lookup, when it differs from
  // `entityId` (Cognism: the redeemed-contact row id; `entityId` there is the redeemId used to
  // save). Omit for types where `entityId` is itself the stored id (e.g. Oscar contacts, buyers).
  savedEntityId?: string;
  defaultValues: Partial<T>;
};

export const entityToDataType = (entity: AllEntities): DataTypeNameWithBuyersAndSuppliers => {
  switch (entity) {
    case "notice":
      return "TENDERS|PRE-TENDERS";
    case "document":
      return "DOCUMENTS";
    case "framework":
      return "FRAMEWORKS";
    case "buyer":
      return "BUYERS";
    case "supplier":
      return "SUPPLIERS";
    case "contact":
    case "contact_cognism":
      return "CONTACTS";
    // TODO: Attach to correct data type when available - if this isn't correct
    case "buyer_activity_report":
      return "BUYERS|SUPPLIERS";
    default:
      throw new Error(`Unknown entity type: ${entity}`);
  }
};

export const getEntityLabel = (entity: AllEntities, capitalise = true) => {
  const pluralEntity = `${entity}s`;

  switch (entity) {
    // Both contact sources read as "contacts" — the source is an internal distinction.
    case "contact_cognism":
      return capitalise ? "Contacts" : "contacts";
    case "notice":
    case "framework":
    case "buyer":
    case "contact":
    case "supplier":
      return capitalise ? capitalize(pluralEntity) : pluralEntity;
    case "document":
      return `${capitalise ? "Strategic" : "strategic"} & planning documents`;
  }
};

export const getEntityEmptyStateDescription = (entity: AllEntities): string | undefined => {
  switch (entity) {
    case "notice":
      return "By adding a notice, you can generate insights from past procurement notices related to this notice, helping you to qualify the lead and gather valuable information for outreach.";
    case "document":
      return "Uncover strategic priorities by exploring procurement strategies, analysing budgets, and aligning with the buyer's social policies to position your solution effectively.";
    case "framework":
      return "Leverage frameworks to unlock new opportunities by checking applicability, assessing eligibility for direct or partnered access, and understanding buyer preferences and routes to market.";
    case "buyer":
      return "Better qualify opportunities by evaluating past awards, analysing deal values, and leveraging your history with the buyer to deepen your understanding.";
    case "supplier":
      return "Add relevant suppliers to assess their relationships with the buyer and leverage your network for insights and potential introductions.";
    case "contact":
    case "contact_cognism":
      return "Identify key decision-makers, start conversations, book meetings to understand their priorities, and build strong relationships to influence the buying process and align your solution.";
    case "buyer_activity_report":
      return "Add relevant buyer activity reports to gain insights into buyer behavior and trends.";
  }
};

export function convertEntityTypeToObjectEntityType<T>(
  entityType: AllEntities,
  TargetEnum: { [key: string]: T },
): T {
  switch (entityType) {
    case "notice":
      return TargetEnum.Notice;
    case "framework":
      return TargetEnum.Framework;
    case "contact":
      return TargetEnum.Contact;
    case "contact_cognism":
      return TargetEnum.ContactCognism;
    case "document":
      return TargetEnum.Document;
    case "buyer":
      return TargetEnum.Buyer;
    case "supplier":
      return TargetEnum.Supplier;
    case "custom_buyer":
      return TargetEnum.CustomOrganisation;
    case "buyer_activity_report":
      return TargetEnum.BuyerActivityReport;
    default:
      throw new Error(`Unknown entity type: ${entityType}`);
  }
}

/**
 * Whether a lead or bid entity is an organisation there as a buyer. One named as an "organisation"
 * is saved in its primary role, and the callers that name one pass buyers, so it counts as a buyer.
 */
export function isBuyerOrganisation(entity: { entityType: string }): boolean {
  return entity.entityType === "buyer" || entity.entityType === "organisation";
}

const ENTITY_TYPE_UI_LABELS: Record<string, string> = {
  notice: "notice",
  framework: "framework",
  contact: "contact",
  contact_cognism: "contact",
  document: "document",
  buyer: "buyer",
  supplier: "supplier",
  organisation: "organisation",
  custom_organisation: "custom organisation",
  buyer_activity_report: "buyer activity report",
};

export function getObjectEntityTypeUILabel(entityType: string): string {
  return ENTITY_TYPE_UI_LABELS[entityType] ?? entityType;
}

export const SavedEntityCardContainer = styled(Flex)<{ padding?: number }>(
  ({ theme, padding = 24 }) => ({
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    padding,
    cursor: "pointer",
    borderRadius: 8,
    border: `1px solid ${theme.colors.sysBorderDecorative}`,

    // Exclude dropdown menu content from triggering hover styles
    "&:hover:not(:has([data-radix-popper-content-wrapper]:hover))": {
      border: `1px solid ${theme.colors.sysPrimaryDefault}`,
      background: theme.colors.sysPrimarySubtle,
    },

    transition: "all 0.2s ease-in-out",
  }),
);

export const SavedEntityCardIcon = styled(Flex)(({ theme }) => ({
  justifyContent: "center",
  alignItems: "center",
  width: 24,
  height: 24,
  background: theme.colors.sysBackgroundAlternative,
  borderRadius: 4,
}));

export const EntityContentWrapper = styled(Flex)({ minWidth: 0 });
